/**
 * Import Sources Service
 * Real API implementation connecting to backend.
 *
 * CRUD + connectivity-test operations for persistent import sources
 * (API / CSV / blockchain). Split out of import.service.ts; bodies moved
 * verbatim.
 */

import { api, throwBackendError } from '@/platform/api/api.service'
import { IMPORT_ROUTES } from '@/shared/constants/config-routes'
import { logger } from '@/shared/utils/logger'

import { DeleteImportSourceResponse, ApiConfig, BlockchainConfig } from '../types'
import {
  type ImportSource,
  type ImportSourceResponse,
  transformToImportSource,
} from './import-service-shared'
import { resolveSourceUuid } from '../utils/resolve-source-uuid'

/**
 * Get all import sources
 */
export const getSources = async (): Promise<ImportSource[]> => {
  // apiService unwraps the envelope: api.get<T>() resolves to the payload (the `data` value). Never access .data on it.
  // The backend returns `{ success, data: ImportSourceResponse[] }`, so after
  // unwrapping `response.data` IS the raw source array (not a nested wrapper).
  const response = await api.get<Record<string, unknown>[]>(IMPORT_ROUTES.SOURCES.LIST)

  // `api.get` NEVER throws — it returns `{ success, data?, error? }`. A
  // previous version only checked `!response.data` (and wrapped everything in
  // an unreachable try/catch), so 401/403/500 errors were silently converted
  // into an empty list. That made it impossible for callers to distinguish
  // "no sources configured" from "failed to load sources". Mirror the
  // `response.success` check used by getJobs()/getImportConfig() and bubble
  // the backend error up so the caller's catch can surface a real message.
  if (!response.success) {
    throwBackendError(response, 'IMPORT_SOURCES_LOAD_FAILED')
  }

  // Safe handling of response data with fallbacks. A successful response with
  // an empty/absent payload legitimately means "no sources configured".
  if (!response.data) {
    return []
  }

  // Transform backend response to frontend format. The current backend returns a
  // bare array; the `.sources` branch is a defensive guard against an alternate
  // `{ sources: [...] }` shape (not observed in this backend, kept for safety).
  const responseData = response.data as Record<string, unknown>[] | Record<string, unknown>
  let sources: Record<string, unknown>[] = []
  if (Array.isArray(responseData)) {
    sources = responseData
  } else if (Array.isArray(responseData.sources)) {
    sources = responseData.sources as Record<string, unknown>[]
  } else {
    return []
  }

  // Transform EVERY source — API, CSV, and blockchain alike. A CSV source is a
  // persisted `data_sources` row (created by a CSV import), NOT a throwaway
  // upload: the backend manages it end-to-end just like the other types —
  //   - rename/pause → PUT /imports/sources/:id (type-agnostic updateSource)
  //   - delete       → DELETE /imports/sources/:id (type-agnostic deleteSource)
  //   - re-import    → POST /imports/sources/:id/sync dispatches `file` →
  //                    executeCsvFileResync (re-runs the cached CSV file)
  // A previous version dropped `type === 'csv'` rows here, which made them
  // invisible in "Importaciones Configuradas" and therefore impossible to
  // pause/rename/delete from the UI. They must render alongside API + blockchain
  // sources so the user can manage them.
  const transformedSources = sources.map(transformToImportSource)

  return transformedSources
}

/**
 * Credential material as the API accepts it. Every credential field is optional
 * because the EXCHANGE's `auth_scheme` — not this client — decides which ones
 * it needs (#2049 · F7): a key + secret venue needs `apiKey` + `secretKey`, a
 * wallet-authenticated one (HyperLiquid) needs `walletAddress` alone.
 */
interface ApiCredentialPayload {
  apiKey?: string
  secretKey?: string
  walletAddress?: string
  sandbox: boolean
}

/**
 * Only the credential fields the user actually filled travel to the server.
 *
 * ⚠️ The route's zod floor (`apiCredentialMaterialSchema.superRefine`) demands
 * one COMPLETE shape — key + secret, OR an address. The old body always sent
 * `apiKey: ''` + `secretKey: ''`, so a wallet exchange would have failed that
 * floor even with a perfect address in the form. In the other direction a blank
 * is not harmless either: the server cannot tell `''` from "the user typed a
 * space", and would store a source authenticated with two empty strings.
 */
const buildApiCredentialPayload = (config: ApiConfig): ApiCredentialPayload => {
  const payload: ApiCredentialPayload = { sandbox: config.sandbox ?? false }
  const carriesContent = (value: string | undefined): boolean => (value ?? '').trim().length > 0

  if (carriesContent(config.apiKey)) payload.apiKey = config.apiKey
  if (carriesContent(config.secretKey)) payload.secretKey = config.secretKey
  if (carriesContent(config.walletAddress)) payload.walletAddress = config.walletAddress
  return payload
}

/**
 * Create new API source
 */
export const createApiSource = async (
  config: ApiConfig,
  options: { acknowledgeDuplicateAccount?: boolean } = {},
): Promise<ImportSource> => {
  const response = await api.post<unknown>(`${IMPORT_ROUTES.SOURCES.BASE}/api`, {
    name: config.medioName, // Backend expects 'name' field
    exchange: config.exchange,
    credentials: buildApiCredentialPayload(config),
    // Sent only after the user ticked the same-account warning (migration
    // 0372); without it the server answers 409 DUPLICATE_ACCOUNT_CONNECTION.
    ...(options.acknowledgeDuplicateAccount === true && { acknowledgeDuplicateAccount: true }),
    // Thread the optional From Date floor. `config.fromDate` is `YYYY-MM-DD`
    // (native <input type="date">); anchor it at midnight UTC to match the
    // EditSourceModal convention. Omit the field entirely when no date is set.
    ...(config.fromDate !== '' && {
      importFromDate: new Date(`${config.fromDate}T00:00:00Z`).toISOString(),
    }),
  })

  // api.post NEVER throws — failures come back as `{ success: false, error }`.
  // Without this check, `transformToImportSource(undefined)` threw a TypeError
  // that destroyed the backend's real error message (e.g. duplicate name).
  if (!response.success) {
    throwBackendError(response, 'IMPORT_SOURCE_CREATE_FAILED')
  }

  return transformToImportSource(response.data as ImportSourceResponse)
}

/**
 * Create new blockchain source
 */
export const createBlockchainSource = async (config: BlockchainConfig): Promise<ImportSource> => {
  const response = await api.post<unknown>(`${IMPORT_ROUTES.SOURCES.BASE}/blockchain`, {
    blockchain: {
      // The chain to create + scan the wallet on is the one the user SELECTED IN
      // THE DROPDOWN, which the form writes to `config.network`. That dropdown
      // lists EVM CHAINS (ethereum / polygon / bsc …), NOT mainnet/testnet
      // networks. `config.blockchain` only holds the provider TILE the user first
      // clicked and stays stale when they then change the dropdown (both are EVM,
      // so the same 0x… address validates on either). The backend resolves the
      // scan chainId from the stored `blockchain` slug (wallet-sync.handler), so
      // sending the tile here would silently create + scan the wallet on the
      // WRONG chain — its transactions (and the resulting tax) would be wrong.
      blockchain: config.network,
      address: config.address,
      medioName: config.medioName,
      // "Sync from" floor — without it the backend scans a 30-day default
      // lookback instead of the user's chosen start date. Mirror the
      // createApiSource guard: omit the field entirely when no date is set
      // (the native <input type="date"> yields '' when empty).
      ...(config.fromDate !== '' && { fromDate: config.fromDate }),
    },
  })

  // See createApiSource: api.post never throws, so bubble the backend error up.
  if (!response.success) {
    throwBackendError(response, 'IMPORT_SOURCE_CREATE_FAILED')
  }

  return transformToImportSource(response.data as ImportSourceResponse)
}

/**
 * Update source
 * @param sourceOrId - Either the ImportSource object (preferred) or numeric ID (deprecated)
 * @param updates - Partial updates to apply
 *
 * Body fields sent to PUT /api/imports/sources/:id:
 * - `name` — only when present in `updates`
 * - `isActive` — derived from `updates.status` when present
 * - `importFromDate` — ISO datetime string or `null`. Distinguish carefully:
 *   `null` clears the floor on the backend, `undefined` leaves it untouched.
 */
export const updateSource = async (
  sourceOrId: ImportSource | string,
  updates: Partial<ImportSource>,
): Promise<ImportSource> => {
  const uuid = resolveSourceUuid(sourceOrId)

  // Build body so we only include keys that were explicitly provided.
  // This preserves the `null` vs `undefined` distinction the backend relies on
  // to know whether to clear `import_from_date` or leave it alone.
  const body: {
    name?: string
    isActive?: boolean
    importFromDate?: string | null
  } = {}

  if (updates.name !== undefined) {
    body.name = updates.name
  }
  if (updates.status !== undefined) {
    body.isActive = updates.status === 'active'
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'importFromDate')) {
    body.importFromDate = updates.importFromDate ?? null
  }

  // UPDATE_SOURCE is a function that takes uuid and returns the URL
  const response = await api.put<unknown>(IMPORT_ROUTES.UPDATE_SOURCE(uuid), body)

  // See createApiSource: api.put never throws, so bubble the backend error up.
  if (!response.success) {
    throwBackendError(response, 'IMPORT_SOURCE_UPDATE_FAILED')
  }

  return transformToImportSource(response.data as ImportSourceResponse)
}

/**
 * Rotate (replace) the encrypted API credentials of an existing API source.
 * Maps to PUT /api/imports/sources/:id/credentials. The backend re-encrypts the
 * new keys at rest; only API-type sources accept this.
 */
export const rotateApiCredentials = async (
  sourceId: string,
  // Same rule as the create body: the exchange's `auth_scheme` decides which
  // fields it needs, so a wallet-authenticated source rotates its ADDRESS and
  // sends no key at all (#2049 · F7). The route schema is the shared
  // `apiCredentialMaterialSchema`, sent FLAT (no `credentials` wrapper).
  credentials: {
    apiKey?: string
    secretKey?: string
    walletAddress?: string
    passphrase?: string
    sandbox?: boolean
    /** The user accepted the same-account warning (409 DUPLICATE_ACCOUNT_CONNECTION). */
    acknowledgeDuplicateAccount?: boolean
  },
): Promise<void> => {
  const response = await api.put<unknown>(
    `${IMPORT_ROUTES.SOURCES.BASE}/${sourceId}/credentials`,
    credentials,
  )
  if (!response.success) {
    throwBackendError(response, 'IMPORT_SOURCE_CREDENTIALS_ROTATE_FAILED')
  }
}

/**
 * Delete source
 * @param sourceOrId - Either the ImportSource object (preferred) or numeric ID (deprecated)
 */
export const deleteSource = async (sourceOrId: ImportSource | string): Promise<void> => {
  const uuid = resolveSourceUuid(sourceOrId)

  // DELETE_SOURCE is a function that takes uuid and returns the URL
  const response = await api.delete<DeleteImportSourceResponse>(IMPORT_ROUTES.DELETE_SOURCE(uuid))

  // api.delete NEVER throws — a failed delete previously resolved silently,
  // so the caller removed the row from state and toasted success while the
  // source still existed on the backend. Surface the real error instead.
  if (!response.success) {
    throwBackendError(response, 'IMPORT_SOURCE_DELETE_FAILED')
  }
}

/** Outcome of a bulk source delete: exactly which ids went, and which did not. */
export interface BulkDeleteSourcesResult {
  deleted: string[]
  failed: string[]
}

/**
 * Delete multiple sources
 * @param sourcesOrIds - Array of ImportSource objects (preferred) or numeric IDs (deprecated)
 */
export const deleteMultipleSources = async (
  sourcesOrIds: (ImportSource | string)[],
): Promise<BulkDeleteSourcesResult> => {
  // Delete each source individually via the single-delete endpoint
  // (apiCall strips body from DELETE requests, so bulk-delete with body doesn't work)
  const results = await Promise.allSettled(sourcesOrIds.map((item) => deleteSource(item)))

  // REPORT the split instead of throwing the first failure away. Deleting N
  // sources is inherently partial: throwing made "two of three are gone" look
  // identical to "nothing happened", so the page said nothing had been deleted
  // while two rows had, and the hook — which prunes local state only after the
  // await — left both on screen pointing at sources the server no longer has.
  const deleted: string[] = []
  const failed: string[] = []
  results.forEach((result, index) => {
    const item = sourcesOrIds[index]
    const id = typeof item === 'object' && item !== null ? item.id : String(item)
    if (result.status === 'fulfilled') deleted.push(id)
    else failed.push(id)
  })

  return { deleted, failed }
}

/**
 * The outcome of a credential test, kept distinguishable.
 *
 * A boolean used to collapse three different situations into one, and the UI
 * then told every one of them "verifica tus credenciales" — including the
 * network outage, which accused credentials that were fine. The exchange's own
 * reason travels in `reason` so the user learns what to actually fix.
 */
export type ApiCredentialTestResult =
  | { outcome: 'connected'; permissions: string[] }
  | { outcome: 'rejected'; reason: string | undefined; code?: string }
  /**
   * The probe never ran: the backend's `502 CREDENTIAL_TEST_FAILED`, or a `400`
   * from the credential gate (`MISSING_CREDENTIAL_FIELD` /
   * `INVALID_WALLET_ADDRESS_FORMAT`, #2049 · F7). `code` travels alongside the
   * reason because the reason is an ENGLISH sentence composed at the throw
   * site, and only the code is stable enough to map onto localized copy.
   */
  | { outcome: 'notCompleted'; reason: string | undefined; code: string | undefined }
  | { outcome: 'unreachable' }

/**
 * Test API credentials against the exchange.
 *
 * THE BACKEND ALREADY SEPARATES THE TWO CASES — and this used to merge them:
 *   `200 {data:{connected:false, error}}` → the exchange ANSWERED and refused
 *   `502 {error:{code:'CREDENTIAL_TEST_FAILED', message}}` → the probe could
 *      not complete (unsupported exchange, no testnet URL, DNS, timeout)
 *
 * ⚠️ `api` here is the api-helper client, which NEVER throws: a failure comes
 * back as `{ success: false, error: { code, message } }` with NO `data`. So the
 * old `reason: response.data?.error` read `undefined` on every 502, the message
 * was dropped, and the caller reported a refusal the exchange never made.
 * Browser-proven 2026-08-09: sandbox + Kraken → "El exchange ha rechazado las
 * credenciales." over a real message of "Exchange kraken does not support
 * testnet". (`apiService.post` DOES throw; these two clients are not
 * interchangeable — read the envelope, do not assume a throw.)
 */
export const testApiCredentials = async (config: ApiConfig): Promise<ApiCredentialTestResult> => {
  try {
    const response = await api.post<{
      connected: boolean
      // what the key may do beyond reading, when the venue can say ("trade")
      keyPowers?: string[]
      code?: string
      error?: string
    }>(IMPORT_ROUTES.API.TEST_CREDENTIALS, {
      exchange: config.exchange,
      credentials: buildApiCredentialPayload(config),
    })

    if (!response.success) {
      return {
        outcome: 'notCompleted',
        reason: response.error?.message,
        code: response.error?.code,
      }
    }
    if (response.data?.connected === true) {
      return { outcome: 'connected', permissions: response.data.keyPowers ?? [] }
    }
    return { outcome: 'rejected', reason: response.data?.error, code: response.data?.code }
  } catch (error) {
    // Transport failure: the exchange never answered, so nothing is known about
    // the credentials and nothing may be claimed about them.
    logger.error('testApiCredentials failed', error)
    return { outcome: 'unreachable' }
  }
}
